import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { motion, AnimatePresence } from "motion/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Calendar, ChevronDown } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";
import {
  getAccountTrades,
  getAccountSnapshots,
  type AccountTrade,
  type AccountSnapshot,
} from "@/lib/api/accounts";
import { getMarketCandles, type MarketCandle } from "@/lib/api/market";

type Range = "1D" | "1W" | "1M" | "ALL";

const RANGE_LABEL: Record<Range, string> = {
  "1D": "1 Day",
  "1W": "1 Week",
  "1M": "1 Month",
  ALL: "All",
};

/** Returns the `since` epoch-ms for a given range. `ALL` returns 0
 *  (i.e. no lower bound) so the worker returns up to the row cap. */
function rangeSince(r: Range): number {
  const now = Date.now();
  switch (r) {
    case "1D":
      return now - 24 * 3600 * 1000;
    case "1W":
      return now - 7 * 24 * 3600 * 1000;
    case "1M":
      return now - 30 * 24 * 3600 * 1000;
    case "ALL":
      return 0;
  }
}

function candleIntervalForRange(r: Range): "5m" | "15m" | "60m" {
  if (r === "1D") return "5m";
  if (r === "1W") return "15m";
  return "60m";
}

/** X-axis label format chosen by range — same rule the screenshot
 *  prop-firm dashboards use: time of day for sub-day ranges, calendar
 *  date for week+, and month/year for the full history view. */
function formatTick(ms: number, r: Range): string {
  const d = new Date(ms);
  if (r === "1D") {
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  if (r === "ALL") {
    return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatTooltipDate(ms: number, r: Range): string {
  const d = new Date(ms);
  if (r === "1D") {
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Account Equity Curve — full-width per-account balance history chart
 * mounted in the Live Account Stats section of /pairs/:slug. Mirrors
 * the visual language every prop firm uses for their funded-trader
 * dashboards: a wandering balance line with horizontal reference
 * floors (max-loss limit, profit target, daily DD), a dark tooltip
 * card, and a range selector.
 *
 * The line is BALANCE, not equity — closed-trade pnl only. Equity
 * oscillates with open P&L and goes dark when the bot is asleep, so
 * a curve plotted from equity ticks looks broken the moment trading
 * pauses. Balance steps up cleanly each close and stays flat between,
 * which matches what every prop firm shows on its trader dashboard.
 *
 * Data flow:
 *   • Mount + range change → REST `/api/accounts/:id/snapshots?since=<r>`
 *     seeds the curve with persisted history. We read `snapshot.balance`
 *     for the primary series — equity is ignored.
 *   • Pre-pend a synthetic anchor at `(anchorDateMs, anchorBalance)`
 *     when both are provided so the chart always extends back to the
 *     account-pair creation date with a flat line at the starting
 *     balance, even before the first trade closes.
 *   • `liveBalance` + `liveEquity` props tick via the WS. The chart
 *     renders TWO lines: a solid blue BALANCE line (historical
 *     closed-trade pnl, what every prop firm anchors its growth
 *     curve on) and a lighter blue dashed EQUITY line layered on
 *     top. Equity == balance when no trade is open — the two lines
 *     overlap. While a trade is open, equity oscillates around
 *     balance and the operator can see the live wiggle without
 *     losing the closed-day-aware "where did my balance actually
 *     end up" reference. This is the same combo professional broker
 *     dashboards use (Tradovate, MetaTrader, FundedNext, etc.).
 *
 * The reference floors (red max-loss, green profit-target, dashed
 * gray daily-DD) are passed in pre-computed against the starting
 * balance so they're stable horizontals — they don't drift with
 * current equity, which would be wrong for a closed-day-aware view.
 */
export function TradingGrowthCurve({
  accountId,
  startBalance,
  anchorDateMs,
  maxLossFloorUsd,
  dailyLossFloorUsd,
  profitTargetUsd,
  liveBalance,
  liveEquity,
  className,
}: {
  /** Opaque slug or raw id — passed straight to the snapshots route. */
  accountId: string;
  /** Anchor for the "0% drawdown" baseline + the synthetic creation-date
   *  seed point. Drawn as a faint horizontal reference. Null = no anchor
   *  (the curve falls back to dataMin / dataMax of whatever snapshots
   *  came back). */
  startBalance: number | null;
  /** Account-pair creation timestamp (ms). When provided alongside
   *  `startBalance`, the chart pre-pends a synthetic point at this date
   *  so a freshly-paired account that hasn't traded yet still shows a
   *  flat line at the starting balance back to "the day you paired
   *  them". Null falls back to dataMin. */
  anchorDateMs: number | null;
  /** USD equity that, if reached, breaches the firm's max-DD rule.
   *  Drawn in red. Null = rule not captured for this account. */
  maxLossFloorUsd: number | null;
  /** Today's daily-DD floor, anchored to the first snapshot of the UTC
   *  day. Drawn dashed gray. */
  dailyLossFloorUsd: number | null;
  /** USD equity that hits the phase profit target. Green. Null on
   *  funded accounts. */
  profitTargetUsd: number | null;
  /** Latest live BALANCE from the WS slice. Drives the primary
   *  (solid) line. */
  liveBalance: number | null;
  /** Latest live EQUITY from the WS slice. Drives the secondary
   *  (dashed, lighter) line layered on top of balance — shows the
   *  open-pnl wiggle while a trade is active and falls back to
   *  balance when flat. */
  liveEquity: number | null;
  className?: string;
}): ReactNode {
  const [range, setRange] = useState<Range>("1W");
  const [snapshots, setSnapshots] = useState<AccountSnapshot[] | null>(null);
  const [trades, setTrades] = useState<AccountTrade[] | null>(null);
  const [candlesBySymbol, setCandlesBySymbol] = useState<Record<string, MarketCandle[]>>({});
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  // Hovered point — drives the traveling crosshair dots on each
  // reference line. Recharts hands us the active payload via
  // onMouseMove; we reach for the first payload item (which carries
  // the full data point — balance + equity).
  const [hover, setHover] = useState<{
    t: number;
    balance: number;
    equity: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingSnapshots(true);
    const since = rangeSince(range);
    getAccountSnapshots(accountId, since > 0 ? { since, limit: 2000 } : { limit: 2000 })
      .then((rows) => {
        if (cancelled) return;
        // The `since`-windowed worker route returns ASC; the bare route
        // returns DESC. Normalise to ASC for the chart.
        const asc = [...rows].sort((a, b) => a.snapshot_at - b.snapshot_at);
        setSnapshots(asc);
      })
      .catch(() => {
        if (!cancelled) setSnapshots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSnapshots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, range]);

  useEffect(() => {
    let cancelled = false;
    const since = rangeSince(range);
    getAccountTrades(accountId)
      .then((rows) => {
        if (cancelled) return;
        setTrades(
          rows
            .filter((t) => t.closed_at != null && t.closed_at >= since)
            .sort((a, b) => a.opened_at - b.opened_at),
        );
      })
      .catch(() => {
        if (!cancelled) setTrades([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, range]);

  useEffect(() => {
    let cancelled = false;
    const closedTrades = (trades ?? []).filter(
      (t) =>
        t.closed_at != null &&
        t.close_price != null &&
        t.pnl != null &&
        t.closed_at > t.opened_at,
    );
    if (closedTrades.length === 0) {
      setCandlesBySymbol({});
      return () => {
        cancelled = true;
      };
    }

    const bySymbol = new Map<string, { from: number; to: number }>();
    for (const t of closedTrades) {
      const current = bySymbol.get(t.symbol);
      const from = t.opened_at - 5 * 60 * 1000;
      const to = (t.closed_at ?? t.opened_at) + 5 * 60 * 1000;
      bySymbol.set(t.symbol, {
        from: current ? Math.min(current.from, from) : from,
        to: current ? Math.max(current.to, to) : to,
      });
    }

    const interval = candleIntervalForRange(range);
    Promise.all(
      Array.from(bySymbol.entries()).map(async ([symbol, window]) => {
        try {
          return [
            symbol,
            await getMarketCandles(symbol, {
              from: window.from,
              to: window.to,
              interval,
            }),
          ] as const;
        } catch {
          return [symbol, []] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setCandlesBySymbol(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [trades, range]);

  // Combine seeded snapshots + creation anchor + latest live tick.
  // Each point carries BOTH balance (closed-trade pnl, the primary
  // reference) and equity (balance + open pnl, the live wiggle).
  // Pre-trade and between trades the two are identical, so the
  // equity line lays directly on top of the balance line. The
  // moment a trade opens, equity diverges and the dashed overlay
  // shows the live oscillation while balance keeps the closed-day
  // anchor visible.
  const chartData = useMemo(() => {
    const data: { t: number; balance: number; equity: number }[] = [];

    // Pre-pend the creation anchor so a freshly-paired account that
    // hasn't traded yet renders as a flat line at the starting balance
    // from "the day you paired them" up to now. Skipped when either
    // input is null — the chart falls back to dataMin in that case.
    if (anchorDateMs != null && startBalance != null) {
      data.push({ t: anchorDateMs, balance: startBalance, equity: startBalance });
    }

    if (snapshots) {
      for (const s of snapshots) {
        // Skip snapshots predating the anchor — the synthetic seed
        // already covers that range with the starting balance.
        if (anchorDateMs != null && s.snapshot_at < anchorDateMs) continue;
        data.push({ t: s.snapshot_at, balance: s.balance, equity: s.equity });
      }
    }

    if (liveBalance != null || liveEquity != null) {
      const now = Date.now();
      const tailIdx = data.length - 1;
      const tail = tailIdx >= 0 ? data[tailIdx] : undefined;
      // Live tail uses live values where available, falls back to
      // tail values otherwise (so a lone live equity tick doesn't
      // erase the historical balance).
      const nextBalance = liveBalance ?? tail?.balance ?? startBalance ?? 0;
      const nextEquity = liveEquity ?? liveBalance ?? tail?.equity ?? startBalance ?? 0;
      if (tail !== undefined && now - tail.t < 2000) {
        data[tailIdx] = { t: now, balance: nextBalance, equity: nextEquity };
      } else {
        data.push({ t: now, balance: nextBalance, equity: nextEquity });
      }
    }

    const baseData = [...data].sort((a, b) => a.t - b.t);
    const balanceAt = (ts: number): number => {
      let last = startBalance ?? baseData[0]?.balance ?? 0;
      for (const p of baseData) {
        if (p.t > ts) break;
        last = p.balance;
      }
      return last;
    };

    const synthetic: { t: number; balance: number; equity: number }[] = [];
    for (const t of trades ?? []) {
      if (t.closed_at == null || t.close_price == null || t.pnl == null) continue;
      if (t.closed_at <= t.opened_at) continue;

      const moveToClose =
        t.side === "buy" ? t.close_price - t.entry_price : t.entry_price - t.close_price;
      if (!Number.isFinite(moveToClose) || Math.abs(moveToClose) < 1e-9) continue;

      const openBalance = balanceAt(t.opened_at);
      const closeBalance = openBalance + t.pnl;
      synthetic.push({ t: t.opened_at, balance: openBalance, equity: openBalance });

      const candles = candlesBySymbol[t.symbol] ?? [];
      for (const candle of candles) {
        if (candle.t <= t.opened_at || candle.t >= t.closed_at) continue;
        const moveNow =
          t.side === "buy" ? candle.close - t.entry_price : t.entry_price - candle.close;
        const estimatedOpenPnl = t.pnl * (moveNow / moveToClose);
        if (!Number.isFinite(estimatedOpenPnl)) continue;
        synthetic.push({
          t: candle.t,
          balance: openBalance,
          equity: openBalance + estimatedOpenPnl,
        });
      }

      synthetic.push({ t: t.closed_at, balance: closeBalance, equity: closeBalance });
    }

    if (synthetic.length === 0) return baseData;
    return [...baseData, ...synthetic].sort((a, b) => a.t - b.t);
  }, [
    snapshots,
    liveBalance,
    liveEquity,
    anchorDateMs,
    startBalance,
    trades,
    candlesBySymbol,
  ]);

  // Y domain — center the START balance on the chart's vertical
  // midline so the operator always sees equal "danger" room below
  // and "growth" headroom above. This is what every real prop-firm
  // dashboard does (FundingPips, FTMO, MyForexFunds, Equity Edge):
  // the start line sits dead-center so the curve has visible runway
  // in BOTH directions, regardless of whether profitTarget is
  // tighter or looser than maxLoss.
  //
  // Algorithm:
  //   1. Compute `half` = the greatest absolute distance from
  //      startBalance to any reference floor / target / curve point.
  //      Mirroring half above and below gives perfect centering.
  //   2. Fall back to 10% of startBalance when nothing else is in
  //      range yet (brand-new pair, no rules captured) — gives the
  //      operator a real growth/loss canvas before any line lands.
  //   3. Pad 2% of the half-range on each side so the curve never
  //      paints exactly on the edge once equity drifts past the
  //      farthest reference line.
  //
  // When startBalance isn't available, fall back to the legacy
  // fit-to-data window so the chart still renders something useful.
  const yDomain = useMemo<[number, number]>(() => {
    if (startBalance == null || !Number.isFinite(startBalance)) {
      let lo: number | null = null;
      let hi: number | null = null;
      const include = (n: number | null | undefined): void => {
        if (n == null || !Number.isFinite(n)) return;
        if (lo == null || n < lo) lo = n;
        if (hi == null || n > hi) hi = n;
      };
      include(maxLossFloorUsd);
      include(profitTargetUsd);
      include(dailyLossFloorUsd);
      for (const p of chartData) {
        include(p.balance);
        include(p.equity);
      }
      if (lo == null || hi == null) return [0, 1];
      if (lo === hi) {
        const eps = Math.max(1, Math.abs(lo) * 0.01);
        return [lo - eps, hi + eps];
      }
      const span = hi - lo;
      return [lo - span * 0.06, hi + span * 0.12];
    }

    let half = 0;
    const offer = (n: number | null | undefined): void => {
      if (n == null || !Number.isFinite(n)) return;
      const d = Math.abs(n - startBalance);
      if (d > half) half = d;
    };
    offer(maxLossFloorUsd);
    offer(profitTargetUsd);
    offer(dailyLossFloorUsd);
    for (const p of chartData) {
      offer(p.balance);
      offer(p.equity);
    }
    if (half === 0) half = Math.max(1, Math.abs(startBalance) * 0.1);
    const pad = half * 0.02;
    return [startBalance - half - pad, startBalance + half + pad];
  }, [chartData, startBalance, maxLossFloorUsd, dailyLossFloorUsd, profitTargetUsd]);

  // Equity overlay only makes sense when it actually diverges from
  // balance — otherwise the dashed line just doubles the solid one
  // and clutters the view. Show it whenever the live equity differs
  // from the live balance, OR when historical snapshots ever had a
  // non-trivial spread (open trades during the window).
  const showEquityOverlay = useMemo(() => {
    if (liveEquity != null && liveBalance != null && Math.abs(liveEquity - liveBalance) > 0.01) {
      return true;
    }
    for (const p of chartData) {
      if (Math.abs(p.equity - p.balance) > 0.01) return true;
    }
    return false;
  }, [chartData, liveEquity, liveBalance]);

  const empty =
    !loadingSnapshots &&
    chartData.length === 0 &&
    liveBalance == null &&
    liveEquity == null;

  return (
    <div
      className={cn(
        "min-w-0 overflow-hidden rounded-xl border border-border/40 bg-card/40 p-3 backdrop-blur-sm sm:p-4",
        className,
      )}
    >
      {/* Header — title + colored legend chips + range selector. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-sans text-sm font-medium text-foreground">
          Account Equity Curve
        </h3>
        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-3 text-[11px] text-muted-foreground sm:flex">
            <LegendChip color="rgb(244 63 94)" label="Max loss limit" />
            <LegendChip color="rgb(59 130 246)" label="Balance" />
            {showEquityOverlay && (
              <LegendChip color="rgb(125 211 252)" label="Equity" dashed />
            )}
            {profitTargetUsd != null && (
              <LegendChip color="rgb(132 204 22)" label="Profit target" />
            )}
            <LegendChip color="rgb(148 163 184)" label="Daily drawdown" />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
                  <Calendar className="h-3.5 w-3.5" />
                  {RANGE_LABEL[range]}
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              }
            />
            <DropdownMenuContent align="end" className="text-xs">
              {(Object.keys(RANGE_LABEL) as Range[]).map((r) => (
                <DropdownMenuItem
                  key={r}
                  onClick={() => setRange(r)}
                  className={cn(range === r && "bg-accent/15 text-accent")}
                >
                  {RANGE_LABEL[r]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="relative h-[260px] w-full">
        <AnimatePresence mode="wait">
          {empty ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-border/40 text-center text-xs text-muted-foreground"
            >
              <p className="font-medium text-foreground">No equity history yet</p>
              <p className="mt-1 max-w-xs text-[11px] text-muted-foreground/70">
                Snapshots populate as soon as the bot opens its first
                position — until then this view stays empty.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key={`chart-${range}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
              className="h-full w-full"
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  margin={{ top: 6, right: 16, bottom: 18, left: 16 }}
                  onMouseMove={(state) => {
                    const active = state as unknown as {
                      activeLabel?: number;
                      activePayload?: {
                        payload?: { t: number; balance: number; equity: number };
                      }[];
                    };
                    const point = active?.activePayload?.[0]?.payload;
                    if (
                      point &&
                      Number.isFinite(point.t) &&
                      Number.isFinite(point.balance) &&
                      Number.isFinite(point.equity)
                    ) {
                      setHover({
                        t: point.t,
                        balance: point.balance,
                        equity: point.equity,
                      });
                    }
                  }}
                  onMouseLeave={() => setHover(null)}
                >
                  <defs>
                    <linearGradient id="growth-equity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(59 130 246)" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="rgb(59 130 246)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke="currentColor"
                    strokeOpacity={0.08}
                    strokeDasharray="2 4"
                  />
                  <XAxis
                    dataKey="t"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    scale="time"
                    tickFormatter={(t) => formatTick(t as number, range)}
                    stroke="currentColor"
                    strokeOpacity={0.25}
                    tick={{ fontSize: 10, fill: "currentColor", fillOpacity: 0.55 }}
                    tickLine={false}
                    minTickGap={48}
                  />
                  <YAxis
                    domain={yDomain}
                    tickFormatter={(v) =>
                      `$${(Number(v) / 1000).toLocaleString(undefined, {
                        maximumFractionDigits: Number(v) >= 100_000 ? 0 : 2,
                      })}k`
                    }
                    stroke="currentColor"
                    strokeOpacity={0.25}
                    tick={{ fontSize: 10, fill: "currentColor", fillOpacity: 0.55 }}
                    tickLine={false}
                    axisLine={false}
                    width={56}
                    orientation="left"
                  />
                  <Tooltip
                    cursor={{
                      stroke: "currentColor",
                      strokeOpacity: 0.35,
                      strokeDasharray: "4 4",
                      strokeWidth: 1,
                    }}
                    content={(props) => {
                      const { active, payload } = props as unknown as {
                        active?: boolean;
                        payload?: GrowthTooltipPayload[];
                      };
                      return (
                        <GrowthTooltip
                          active={active}
                          payload={payload}
                          range={range}
                          startBalance={startBalance}
                          maxLossFloorUsd={maxLossFloorUsd}
                          dailyLossFloorUsd={dailyLossFloorUsd}
                          profitTargetUsd={profitTargetUsd}
                          showEquity={showEquityOverlay}
                        />
                      );
                    }}
                  />

                  {/* Reference lines drawn before the curve so the live
                      line sits on top. */}
                  {startBalance != null && (
                    <ReferenceLine
                      y={startBalance}
                      stroke="currentColor"
                      strokeOpacity={0.25}
                      strokeDasharray="4 6"
                    />
                  )}
                  {maxLossFloorUsd != null && (
                    <ReferenceLine
                      y={maxLossFloorUsd}
                      stroke="rgb(244 63 94)"
                      strokeOpacity={0.85}
                    />
                  )}
                  {profitTargetUsd != null && (
                    <ReferenceLine
                      y={profitTargetUsd}
                      stroke="rgb(132 204 22)"
                      strokeOpacity={0.85}
                    />
                  )}
                  {dailyLossFloorUsd != null && (
                    <ReferenceLine
                      y={dailyLossFloorUsd}
                      stroke="rgb(148 163 184)"
                      strokeOpacity={0.7}
                      strokeDasharray="4 4"
                    />
                  )}

                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke="none"
                    fill="url(#growth-equity)"
                    isAnimationActive={false}
                  />
                  {showEquityOverlay && (
                    <Line
                      type="monotone"
                      dataKey="equity"
                      stroke="rgb(125 211 252)"
                      strokeWidth={1.25}
                      strokeDasharray="3 3"
                      strokeOpacity={0.85}
                      dot={false}
                      isAnimationActive={false}
                      activeDot={{ r: 3, strokeWidth: 0 }}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="balance"
                    stroke="rgb(59 130 246)"
                    strokeWidth={1.75}
                    dot={false}
                    isAnimationActive={false}
                    activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--background)" }}
                  />

                  {/* Traveling crosshair dots at the hovered x —
                      anchor each colored marker on its respective
                      reference line so the operator can read every
                      level at once, like the prop-firm screenshot. */}
                  {hover && maxLossFloorUsd != null && (
                    <ReferenceDot
                      x={hover.t}
                      y={maxLossFloorUsd}
                      r={4}
                      fill="rgb(244 63 94)"
                      stroke="var(--background)"
                      strokeWidth={2}
                    />
                  )}
                  {hover && profitTargetUsd != null && (
                    <ReferenceDot
                      x={hover.t}
                      y={profitTargetUsd}
                      r={4}
                      fill="rgb(132 204 22)"
                      stroke="var(--background)"
                      strokeWidth={2}
                    />
                  )}
                  {hover && dailyLossFloorUsd != null && (
                    <ReferenceDot
                      x={hover.t}
                      y={dailyLossFloorUsd}
                      r={4}
                      fill="rgb(148 163 184)"
                      stroke="var(--background)"
                      strokeWidth={2}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* No bottom-right footer — the LiveCells row above the chart
          is the canonical source for current Balance / Equity / P&L,
          and the tooltip on hover gives precise per-timestamp values.
          A second redundant footer was confusing on inactive accounts
          where it could fall back to startBalance and read "wrong". */}
    </div>
  );
}

function LegendChip({
  color,
  label,
  dashed,
}: {
  color: string;
  label: string;
  dashed?: boolean;
}): ReactNode {
  return (
    <span className="inline-flex items-center gap-1.5">
      {dashed ? (
        <span
          className="inline-block h-0 w-3 border-t-2"
          style={{ borderColor: color, borderTopStyle: "dashed" }}
        />
      ) : (
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      )}
      <span>{label}</span>
    </span>
  );
}

interface GrowthTooltipPayload {
  value: number;
  payload: { t: number; balance: number; equity: number };
}

function GrowthTooltip({
  active,
  payload,
  range,
  startBalance,
  maxLossFloorUsd,
  dailyLossFloorUsd,
  profitTargetUsd,
  showEquity,
}: {
  active?: boolean;
  payload?: GrowthTooltipPayload[];
  range: Range;
  startBalance: number | null;
  maxLossFloorUsd: number | null;
  dailyLossFloorUsd: number | null;
  profitTargetUsd: number | null;
  showEquity: boolean;
}): ReactNode {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-xl border border-border bg-background/95 px-3.5 py-2.5 text-xs shadow-xl backdrop-blur-md">
      <p className="mb-2 font-medium text-foreground">
        {formatTooltipDate(point.t, range)}
      </p>
      <div className="grid gap-1.5">
        {maxLossFloorUsd != null && (
          <TooltipRow
            color="rgb(244 63 94)"
            label="Max loss limit"
            value={maxLossFloorUsd}
          />
        )}
        <TooltipRow color="rgb(59 130 246)" label="Balance" value={point.balance} />
        {showEquity && (
          <TooltipRow
            color="rgb(125 211 252)"
            label="Equity"
            value={point.equity}
          />
        )}
        {profitTargetUsd != null && (
          <TooltipRow
            color="rgb(132 204 22)"
            label="Profit target"
            value={profitTargetUsd}
          />
        )}
        {dailyLossFloorUsd != null && (
          <TooltipRow
            color="rgb(148 163 184)"
            label="Daily drawdown"
            value={dailyLossFloorUsd}
          />
        )}
        {startBalance != null && (
          <TooltipRow
            color="currentColor"
            label="Start balance"
            value={startBalance}
            muted
          />
        )}
      </div>
    </div>
  );
}

function TooltipRow({
  color,
  label,
  value,
  muted = false,
}: {
  color: string;
  label: string;
  value: number;
  muted?: boolean;
}): ReactNode {
  return (
    <div className="flex items-center justify-between gap-6 text-[11px]">
      <span
        className={cn(
          "inline-flex items-center gap-2",
          muted ? "text-muted-foreground/60" : "text-muted-foreground",
        )}
      >
        <span
          className={cn("h-2 w-2 rounded-full", muted && "opacity-50")}
          style={{ background: color }}
        />
        {label}
      </span>
      <span
        className={cn(
          "font-mono tabular-nums",
          muted ? "text-muted-foreground" : "font-medium text-foreground",
        )}
      >
        {fmtMoney(value)}
      </span>
    </div>
  );
}
